import * as i0 from '@angular/core';
import { InjectionToken, SecurityContext, Injectable, Inject, Pipe, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import dompurify from 'dompurify';
import * as i2 from '@angular/platform-browser';

/**
 * Token for {@link NgDompurifyConfig}
 */
const DOMPURIFY_CONFIG = new InjectionToken('Config for DOMPurify', {
    factory: () => ({}),
    providedIn: 'root',
});

/**
 * Token for adding hooks to DOMPurify, see {@link addHook}
 */
const DOMPURIFY_HOOKS = new InjectionToken('Hooks for DOMPurify', {
    factory: () => [],
    providedIn: 'root',
});

/**
 * A function that takes style rule value as input and returns a sanitized string
 *
 * NOTE: Angular 10 removed CSS sanitation so by default this method does nothing
 */
const SANITIZE_STYLE = new InjectionToken('A function that sanitizes value for a CSS rule', {
    factory: () => value => value,
    providedIn: 'root',
});

const createDOMPurify = dompurify;
/**
 * Implementation of Angular {@link Sanitizer} purifying via DOMPurify
 *
 * use {@link DOMPURIFY_CONFIG} token to provide config ({@link NgDompurifyConfig})
 * use {@link SANITIZE_STYLE} token to provide a style sanitizing method ({@link SanitizeStyle})
 * use {@link DOMPURIFY_HOOKS} token to provide a hooks for DOMPurify ({@link addHook})
 *
 * Ambient type cannot be used without @dynamic https://github.com/angular/angular/issues/23395
 * @dynamic
 */
class NgDompurifySanitizer {
    constructor(config, sanitizeStyle, { defaultView }, hooks) {
        this.config = config;
        this.sanitizeStyle = sanitizeStyle;
        this.domPurify = createDOMPurify(defaultView);
        hooks.forEach(({ name, hook }) => {
            this.domPurify.addHook(name, hook);
        });
    }
    sanitize(context, value, config = this.config) {
        if (context === SecurityContext.SCRIPT) {
            throw new Error('DOMPurify does not support SCRIPT context');
        }
        return context === SecurityContext.STYLE
            ? this.sanitizeStyle(String(value))
            : this.domPurify.sanitize(String(value || ''), config);
    }
}
NgDompurifySanitizer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifySanitizer, deps: [{ token: DOMPURIFY_CONFIG }, { token: SANITIZE_STYLE }, { token: DOCUMENT }, { token: DOMPURIFY_HOOKS }], target: i0.ɵɵFactoryTarget.Injectable });
NgDompurifySanitizer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifySanitizer, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifySanitizer, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOMPURIFY_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SANITIZE_STYLE]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOMPURIFY_HOOKS]
                }] }]; } });

/**
 * Pipe that transforms dirty content to clean via {@link NgDompurifySanitizer}
 */
class NgDompurifyPipe {
    constructor(sanitizer, domSanitizer) {
        this.sanitizer = sanitizer;
        this.domSanitizer = domSanitizer;
    }
    transform(value, context = SecurityContext.HTML, config) {
        return this.bypassSecurityTrust(context, this.sanitizer.sanitize(context, value, config));
    }
    bypassSecurityTrust(context, purifiedValue) {
        switch (context) {
            case SecurityContext.HTML:
                return this.domSanitizer.bypassSecurityTrustHtml(purifiedValue);
            case SecurityContext.STYLE:
                return this.domSanitizer.bypassSecurityTrustStyle(purifiedValue);
            case SecurityContext.URL:
                return this.domSanitizer.bypassSecurityTrustUrl(purifiedValue);
            case SecurityContext.RESOURCE_URL:
                return this.domSanitizer.bypassSecurityTrustResourceUrl(purifiedValue);
            default:
                return null;
        }
    }
}
NgDompurifyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyPipe, deps: [{ token: NgDompurifySanitizer }, { token: i2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
NgDompurifyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyPipe, name: "dompurify" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'dompurify' }]
        }], ctorParameters: function () { return [{ type: NgDompurifySanitizer }, { type: i2.DomSanitizer }]; } });

class NgDompurifyModule {
}
NgDompurifyModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgDompurifyModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyModule, declarations: [NgDompurifyPipe], exports: [NgDompurifyPipe] });
NgDompurifyModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: NgDompurifyModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NgDompurifyPipe],
                    exports: [NgDompurifyPipe],
                }]
        }] });

/**
 * Public API Surface of @tinkoff/ng-dompurify
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DOMPURIFY_CONFIG, DOMPURIFY_HOOKS, NgDompurifyModule, NgDompurifyPipe, NgDompurifySanitizer, SANITIZE_STYLE };
